import React, { useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Wallet, CreditCard, CheckCircle, Mail } from 'lucide-react';
import { createTransaction } from '../database/Functions';
import { useNavigate } from 'react-router-dom';

const RequestWithdrawal = ({ userProfile }) => {
  const [amount, setAmount] = useState(0);
  // const [isAccountSetUp, setIsAccountSetUp] = useState(false); // Stripe-only
  const [isProcessing, setIsProcessing] = useState(false);
  const [paypalEmail, setPaypalEmail] = useState(userProfile?.email || '');
  const [confirmPaypalEmail, setConfirmPaypalEmail] = useState('');
  const functions = getFunctions();
  const navigate = useNavigate()
  // Old Stripe connected account check (commented out)
  // useEffect(() => {
  //   setIsAccountSetUp(!!userProfile.stripeConnectedAccountId);
  // }, [userProfile]);
  // For PayPal flow: no connected account requirement

  const handleWithdrawal = async (event) => {
    event.preventDefault();

    if (!amount || amount <= 0) {
      alert('Please enter a valid amount.');
      return;
    }

    if (amount < 10) {
      alert('Minimum withdrawal amount is $10');
      return;
    }

    if (amount > userProfile.account_balance) {
      alert('Withdrawal amount exceeds your account balance.');
      return;
    }

    if (!paypalEmail) {
      alert('Please enter your PayPal email.');
      return;
    }

    if (paypalEmail !== confirmPaypalEmail) {
      alert('PayPal emails do not match.');
      return;
    }

    setIsProcessing(true);

    const amountToWithdraw = parseFloat(amount);
    const newBalance = (userProfile?.account_balance || 0) - amountToWithdraw;

    const createPaypalWithdrawal = httpsCallable(functions, 'createPaypalWithdrawal');
    try {
      await createPaypalWithdrawal({ userId: userProfile.id || userProfile.uid, amount: amountToWithdraw, paypalEmail });

      try {
        createTransaction(
          userProfile.id || userProfile.uid,
          'Withdrawal',
          'Withdrawal Requested',
          userProfile.account_balance,
          newBalance,
          amountToWithdraw,
          {
            status: 'processing',
            amount: amountToWithdraw,
            timestamp: new Date().toISOString(),
            type: 'withdrawal',
            paypalEmail,
          }
        );
      } catch (e) {
        console.error('Error recording withdrawal transaction', e);
      }

       // 🟣 Send email notification through Formspree
      try {
        await fetch("https://formspree.io/f/mldpagdv", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            subject: `Withdrawal Request from ${userProfile?.full_name}`,
            message: `
              A new withdrawal request has been submitted:
              User: ${userProfile?.full_name}
              Email: ${userProfile?.email || "N/A"}
              PayPal: ${paypalEmail}
              Amount: $${amountToWithdraw}
              Current Balance: $${userProfile?.account_balance}
              New Balance (after withdrawal): $${newBalance}
              Requested At: ${new Date().toLocaleString()}
              User ID: ${userProfile?.id || userProfile?.uid}
            `,
          }),
        });
      } catch (error) {
        console.error("Error sending Formspree email:", error);
      }

      alert('Withdrawal to Paypal requested successfully. Your paypal funds will be processed within 1-3 business days.');
      setAmount(0);
      setConfirmPaypalEmail('');
      navigate("/")
    } catch (error) {
      console.error('Error requesting withdrawal:', error);
      console.log('The error is ', error);
      try {
        createTransaction(
          userProfile.id || userProfile.uid,
          'Withdrawal',
          'Withdrawal Error',
          userProfile.account_balance,
          userProfile.account_balance,
          amountToWithdraw,
          {
            status: 'failed',
            amount: amountToWithdraw,
            timestamp: new Date().toISOString(),
            type: 'withdrawal',
            paypalEmail,
          }
        );
      } catch (e) {
        console.error('Error recording withdrawal error transaction', e);
      }
      alert('Error requesting withdrawal.');
      navigate("/")
    } finally {
      setIsProcessing(false);
    }
  };

  // Old Stripe setup flow (commented out)
  // const handleSetUpAccount = async () => {
  //   setIsSettingUp(true);
  //   try {
  //     const setupStripeAccountFunc = httpsCallable(functions, 'setupStripeAccount');
  //     const result = await setupStripeAccountFunc({ userId: userProfile.id });
  //     if (result.data.success) {
  //       window.location.href = result.data.url;
  //     } else {
  //       alert('Failed to set up Stripe account');
  //     }
  //   } catch (error) {
  //     console.error('Error setting up Stripe account:', error);
  //     alert('Error setting up Stripe account: ' + error.message);
  //   } finally {
  //     setIsSettingUp(false);
  //   }
  // };

  // Stripe account setup UI removed for PayPal flow

  return (
    <div>
      <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
        <Wallet className="w-6 h-6 text-emerald-400 mr-2" />
        Request Withdrawal
      </h2>
      
      <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl border border-gray-800 p-6">
        <div className="flex flex-col md:flex-row gap-8">
          <div className="w-full md:w-2/3">
            <form onSubmit={handleWithdrawal} className="space-y-6">
              <div className="flex items-center bg-emerald-500/20 p-4 rounded-lg mb-6">
                <CheckCircle className="w-5 h-5 text-emerald-400 mr-2" />
                <p className="text-emerald-400 font-medium">Your withdrawal account is set up and ready to use.</p>
              </div>
              
              <div className="mb-6">
                <h3 className="text-xl font-bold text-white mb-2">Current Balance</h3>
                <p className="text-3xl font-bold text-emerald-400">${parseFloat(userProfile.account_balance || 0).toFixed(2)}</p>
              </div>
              
              <div>
                <label className="block text-lg font-medium text-white mb-2">Withdrawal Amount</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <CreditCard className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(parseFloat(e.target.value) || '')}
                    className="block w-full pl-12 pr-4 py-3 rounded-lg bg-gray-800/70 border border-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    placeholder="Enter amount to withdraw"
                    min="10"
                    max={userProfile.account_balance}
                  />
                </div>
                <p className="text-sm text-gray-400 mt-2">Minimum withdrawal: $10</p>
              </div>

              <div>
                <label className="block text-lg font-medium text-white mb-2">PayPal Email</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="email"
                    value={paypalEmail}
                    onChange={(e) => setPaypalEmail(e.target.value)}
                    className="block w-full pl-12 pr-4 py-3 rounded-lg bg-gray-800/70 border border-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    placeholder="Enter your PayPal email"
                    autoComplete="email"
                  />
                </div>
              </div>

              <div>
                <label className="block text-lg font-medium text-white mb-2">Confirm PayPal Email</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="email"
                    value={confirmPaypalEmail}
                    onChange={(e) => setConfirmPaypalEmail(e.target.value)}
                    className="block w-full pl-12 pr-4 py-3 rounded-lg bg-gray-800/70 border border-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    placeholder="Re-enter your PayPal email"
                    autoComplete="email"
                  />
                </div>
                {confirmPaypalEmail && paypalEmail !== confirmPaypalEmail && (
                  <p className="text-sm text-red-400 mt-2">Emails do not match.</p>
                )}
              </div>
              
              <button 
                type="submit" 
                disabled={isProcessing || !amount || amount < 10 || amount > userProfile.account_balance}
                className="w-full bg-emerald-500 hover:bg-emerald-600 text-white py-3 px-4 rounded-lg transition-colors font-medium flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isProcessing ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white mr-2"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    <Wallet className="w-5 h-5 mr-2" />
                    Withdraw ${amount || '0.00'} To Paypal
                  </>
                )}
              </button>
            </form>
          </div>
          
          <div className="w-full md:w-1/3 bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700">
            <h3 className="text-lg font-medium text-white mb-4">Withdrawal Information</h3>
            
            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-medium text-gray-300 mb-1">Processing Time</h4>
                <p className="text-sm text-white">1-3 business days</p>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-gray-300 mb-1">Withdrawal Method</h4>
                <p className="text-sm text-white">Bank Transfer</p>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-gray-300 mb-1">Fees</h4>
                <p className="text-sm text-white">No fees for standard withdrawals</p>
              </div>
              
              <div className="border-t border-gray-700 my-2 pt-4">
                <h4 className="text-sm font-medium text-gray-300 mb-1">Need Help?</h4>
                <p className="text-sm text-gray-400">
                  If you have any questions about withdrawals, please contact our support team.
                </p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Stripe account setup section removed for PayPal withdrawal flow */}
      </div>
    </div>
  );
};

export default RequestWithdrawal;
